/**
 * Apply with Ari feature gate (TP-498).
 *
 * Uses `__CLOUD_ENV__` (not config.isProduction / NODE_ENV) so staging/local
 * stay testable while production stays locked until the global toggle is on.
 * Override users in featureFlagOverrideUserIds can exercise the flow on
 * real production before the global flag flips.
 */

import featureToggles from 'featureToggles';

/**
 * Whether this user is allow-listed to exercise gated features on production
 * while the global toggle is still off.
 *
 * @param {{ id?: number }|null|undefined} user
 * @returns {boolean}
 */
export function isFeatureFlagOverrideUser(user) {
  if (!user || user.id == null) return false;
  const ids = featureToggles.featureFlagOverrideUserIds || [];
  return ids.includes(user.id);
}

/**
 * Whether Apply with Ari is enabled for this viewer.
 *
 * Rules:
 * - Non-production `__CLOUD_ENV__` (local, staging, …): always on
 * - Production: on if `featureToggles.applyWithAri` OR user is in
 *   `featureFlagOverrideUserIds`
 *
 * Pass `user` when available (null/undefined for guests). Guests never match
 * the override list; they only see the feature on production when the global
 * toggle is true.
 *
 * @param {{ id?: number }|null|undefined} user
 * @returns {boolean}
 */
export function isApplyWithAriEnabled(user) {
  return (
    !/production/.test(__CLOUD_ENV__) ||
    !!featureToggles.applyWithAri ||
    isFeatureFlagOverrideUser(user)
  );
}

/**
 * Whether a logged-in user should get the Apply with Ari entry / chat
 * (production QA allow-list). Normal logged-in apply UI is unchanged for
 * everyone else until TP-499.
 *
 * @param {{ id?: number }|null|undefined} user
 * @returns {boolean}
 */
export function canAccessApplyAriWhenLoggedIn(user) {
  return isFeatureFlagOverrideUser(user) && isApplyWithAriEnabled(user);
}
